import * as React from 'react';

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useTranslation} from 'react-i18next';

import PayrollWizardCard from '../../components/Dashboard/PayrollWizardCard';
import DashboardStatCards from '../../components/Dashboard/DashboardStatCards';
import PayrollCostChart from '../../components/Dashboard/PayrollCostChart';
import AttentionList from '../../components/Dashboard/AttentionList';
import BranchStatusTable from '../../components/Dashboard/BranchStatusTable';
import {MobilePeriodBranchFilterBar} from '../../components/Dashboard/MobilePeriodBranchFilterBar';
import {useDashboard} from '../../hooks/useDashboard';
import type {Module} from '../../types/modules';

interface DashboardProps {
    readonly modules?: Module[];
    readonly loading?: boolean;
    readonly error?: string | null;
}

const CARD_SX = {
    p: {xs: 2, sm: 3},
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    height: '100%',
};

export default function Dashboard({error: modulesError = null}: Readonly<DashboardProps>) {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        userName,
        selectedPeriod,
        selectedPeriodLabel,
        loading,
        stats,
        wizardSteps,
        activeStepIndex,
        wizardStatusLabel,
        isWizardComplete,
        attentionItems,
        costHistory,
        costHistoryLoading,
        branchStatusRows,
        onContinuePreparation,
    } = useDashboard();

    return (
        <Box sx={{pb: isMobile ? 9 : 0}}>
            {/* ── Header: greeting + primary CTA (period/branch filters live in the top toolbar) ── */}
            <Stack
                direction={{xs: 'column', md: 'row'}}
                alignItems={{xs: 'stretch', md: 'center'}}
                justifyContent="space-between"
                gap={2}
                sx={{mb: isMobile ? 2 : 3}}
            >
                <Box sx={{textAlign: 'left'}}>
                    <Typography variant="h5" sx={{fontWeight: 700}}>
                        {t('dashboard.welcome', {name: userName ?? ''})}
                    </Typography>
                    <Typography variant="body1" sx={{color: 'text.secondary'}}>
                        {selectedPeriodLabel
                            ? t('dashboard.subtitle', {period: selectedPeriodLabel})
                            : t('dashboard.subtitleNoPeriod')}
                    </Typography>
                </Box>
                {!isMobile && (
                    <Button
                        variant="contained"
                        color="secondary"
                        endIcon={<ArrowForwardIcon/>}
                        onClick={onContinuePreparation}
                        disabled={!selectedPeriod}
                    >
                        {t('dashboard.continuePreparation')}
                    </Button>
                )}
            </Stack>

            {isMobile && <MobilePeriodBranchFilterBar/>}

            {modulesError && (
                <Alert severity="error" sx={{mb: 2}}>
                    {modulesError}
                </Alert>
            )}

            {loading ? (
                <Stack spacing={2.5}>
                    <Skeleton variant="rectangular" height={140} sx={{borderRadius: 1}}/>
                    <Skeleton variant="rectangular" height={100} sx={{borderRadius: 1}}/>
                    <Skeleton variant="rectangular" height={320} sx={{borderRadius: 1}}/>
                    <Skeleton variant="rectangular" height={220} sx={{borderRadius: 1}}/>
                </Stack>
            ) : (
                <Stack spacing={2.5}>
                    <PayrollWizardCard
                        periodLabel={selectedPeriodLabel}
                        statusLabel={wizardStatusLabel}
                        isComplete={isWizardComplete}
                        steps={wizardSteps}
                        activeStepIndex={activeStepIndex}
                        onViewSteps={onContinuePreparation}
                    />

                    <DashboardStatCards stats={stats}/>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', md: '2fr 1fr'},
                        gap: 2.5,
                    }}>
                        <Card sx={CARD_SX}>
                            <CardContent>
                                <PayrollCostChart data={costHistory} loading={costHistoryLoading}/>
                            </CardContent>
                        </Card>
                        <Card sx={CARD_SX}>
                            <CardContent>
                                <AttentionList items={attentionItems}/>
                            </CardContent>
                        </Card>
                    </Box>

                    {isMobile ? (
                        <Box>
                            <BranchStatusTable rows={branchStatusRows} isMobile={isMobile}/>
                        </Box>
                    ) : (
                        <Card sx={CARD_SX}>
                            <CardContent>
                                <BranchStatusTable rows={branchStatusRows} isMobile={isMobile}/>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            )}

            {isMobile && (
                <Box sx={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: 1.5,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    zIndex: (t2) => t2.zIndex.appBar,
                }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        endIcon={<ArrowForwardIcon/>}
                        onClick={onContinuePreparation}
                        disabled={!selectedPeriod}
                    >
                        {t('dashboard.continuePreparation')}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
